import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes'
import { Capacitor } from '@capacitor/core'
import { authGuard, guestGuard } from './guards/auth'
import { sessionGuard } from './guards/session'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  // Respeitar o `base` do Vite (em produção fica `/app/`)
  // para que rotas como `/app/login` casem com `/login`.
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 }
  }
})

function normalizeMobilePath(path) {
  if (!path) return '/'

  let normalized = path
  while (normalized === '/app' || normalized.startsWith('/app/')) {
    normalized = normalized.slice(4) || '/'
  }

  return normalized || '/'
}

function isPasswordRecoveryPath(path) {
  return path === '/forgot-password' || path.startsWith('/reset-password/')
}

// Guard para apps móveis (Capacitor): manter a navegação no módulo /usuario
// e garantir que o primeiro acesso vá para /usuario/login quando não autenticado.
router.beforeEach((to, _from, next) => {
  const isMobileApp = Capacitor.isNativePlatform() || import.meta.env.VITE_IS_MOBILE_APP === 'true'
  if (!isMobileApp) {
    next()
    return
  }

  const normalizedPath = normalizeMobilePath(to.path)

  // Deep links podem chegar com prefixo /app no path mesmo em runtime nativo.
  // Reescreve para a rota SPA correta antes de aplicar bloqueios mobile.
  if (normalizedPath !== to.path && isPasswordRecoveryPath(normalizedPath)) {
    console.log('[DeepLinkDebug][Router] corrigindo path com prefixo /app', {
      originalPath: to.path,
      normalizedPath,
      fullPath: to.fullPath
    })
    next({
      path: normalizedPath,
      query: to.query,
      hash: to.hash,
      replace: true
    })
    return
  }

  if (to.meta?.allowOutsideUsuario) {
    if (isPasswordRecoveryPath(normalizedPath)) {
      console.log('[DeepLinkDebug][Router] allowOutsideUsuario para recuperação de senha', {
        routeName: to.name,
        path: to.path,
        normalizedPath
      })
    }
    next()
    return
  }

  // Fallback extra: se por qualquer motivo a rota ainda não carregou meta,
  // liberamos explicitamente os caminhos de recuperação.
  if (isPasswordRecoveryPath(normalizedPath)) {
    console.log('[DeepLinkDebug][Router] fallback liberado para recuperação de senha', {
      routeName: to.name,
      path: to.path,
      normalizedPath,
      hasAllowOutside: !!to.meta?.allowOutsideUsuario
    })
    next()
    return
  }

  if (normalizedPath.startsWith('/usuario')) {
    next()
    return
  }

  console.log('[DeepLinkDebug][Router] redirecionando para login mobile', {
    routeName: to.name,
    path: to.path,
    fullPath: to.fullPath,
    normalizedPath,
    hasAllowOutside: !!to.meta?.allowOutsideUsuario
  })
  next({ path: '/usuario/login', query: { redirect: to.fullPath } })
})

// Aplicar guards globais
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Verificar se a rota requer autenticação
  if (to.meta.requiresAuth) {
    await authGuard(to, from, next)
  } else if (to.meta.guestOnly) {
    // Se for uma rota apenas para convidados (login, register, etc.)
    await guestGuard(to, from, next)
  } else {
    // Se não requer autenticação, verificar se o usuário está logado
    // para redirecionar do login para dashboard
    if (to.name === 'Login' && authStore.isAuthenticated) {
      next({ name: 'Dashboard' })
      return
    }
    next()
  }
})

// Guard global de sessão
router.beforeEach(sessionGuard)

export default router
